<template>
  <div>
    <Head :title="form.summary" />
    <h1 class="mb-8 text-3xl font-bold">
      <Link class="text-indigo-400 hover:text-indigo-600" href="/tickets/tickets">
        {{ $t('message.tickets.tickets', 2) }}
      </Link>
      <span class="text-indigo-400 font-medium">/</span>
      {{ form.summary }} - {{ ticket.ticket_id }}
    </h1>
    <trashed-message v-if="ticket.deleted_at" class="mb-6" @restore="restore"> This ticket has been deleted. </trashed-message>
    <div class="max-w-3xl bg-white rounded-md shadow overflow-hidden">
      <form @submit.prevent="update">
        <div class="flex flex-wrap -mb-8 -mr-6 p-8">
          <select-input v-model="form.issue_id" :error="form.errors.issue_id" class="lg pb-8 pr-6 w-full" :label="$t('message.tickets.issue')">
            <option :value="null" />
            <option v-for="issue in issues" :key="issue.id" :value="issue.id">{{ issue.name }}</option>
          </select-input>
          <textarea-input v-model="form.summary" :error="form.errors.summary" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.tickets.subject')" />
          <textarea-input v-model="form.description" :error="form.errors.description" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.tickets.description')" />
          <select-input v-if="hasRole(['admin', 'it'])" v-model="form.status" :error="form.errors.status" class="lg pb-8 pr-6 w-full" :label="$t('message.general.status')">
            <option :value="null" />
            <option v-for="st in status" :key="st.id" :value="st.id">
              <!-- {{ st.name }} -->
              {{ $t(`message.tickets.${slugifyStatus(st.name)}`) }}
            </option>
          </select-input>
          <div class="file-attachments flex flex-row flex-wrap items-center">
            <div v-for="(attachment, index) in form.attachments" :key="index" class="mb-4 mr-4">
              <div class="flex items-center justify-between p-2">
                <div class="flex-1 pr-1">
                  <label class="flex items-center justify-center">
                    {{ attachment.name }}
                  </label>
                </div>
                <button v-if="attachment.can_delete == null || attachment.can_delete" type="button" class="mr-1 px-4 py-1 text-white text-xs font-medium hover:bg-gray-700 bg-red-500 rounded-sm" @click="remove(index)">
                  {{ $t('message.general.delete') }}
                </button>
                <a class="px-4 py-1 text-white text-xs font-medium bg-gray-500 hover:bg-gray-700 rounded-sm" :href="attachment.url" download>
                  {{ $t('message.tickets.download') }}
                </a>
              </div>
            </div>
            <div class="flex items-center justify-between mb-4 mr-4 p-2">
              <div class="flex-1 pr-1">
                <input hidden="true" ref="file" type="file" :accept="accept" class="hidden" @change="change" />

                <label class="flex items-center justify-center">
                  {{ $t('message.tickets.add_attachment') }}
                </label>
              </div>
              <button type="button" class="mr-1 px-4 py-1 text-white text-xs font-medium bg-gray-500 hover:bg-gray-700 rounded-sm" @click="importAttachment">
                {{ $t('message.tickets.import') }}
              </button>
            </div>
          </div>
        </div>
        <div class="flex items-center px-8 py-4 bg-gray-50 border-t border-gray-100">
          <button v-if="!ticket.deleted_at" class="text-red-600 hover:underline" tabindex="-1" type="button" @click="destroy">
            {{ $t('message.tickets.delete_ticket') }}
          </button>
          <loading-button :loading="form.processing" class="btn-indigo ml-auto" type="submit">
            {{ $t('message.tickets.update_ticket') }}
          </loading-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import { Icon } from '@iconify/vue'
import FileInput from '@/Shared/FileInput'
import Layout from '@/Shared/Layout'
import TextareaInput from '@/Shared/TextareaInput.vue'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import TrashedMessage from '@/Shared/TrashedMessage'
import PermissionChecker from '../../../Shared/Helpers/PermissionChecker'
import RoleChecker from '../../../Shared/Helpers/RoleChecker'

export default {
  components: {
    Head,
    Icon,
    Link,
    LoadingButton,
    TextareaInput,
    SelectInput,
    TrashedMessage,
    PermissionChecker,
    RoleChecker,
    FileInput,
  },
  layout: Layout,
  props: {
    ticket: Object,
    issues: Array,
    departments: Array,
    status: Array,
  },
  remember: 'form',
  data() {
    return {
      form: this.$inertia.form({
        summary: this.ticket.summary,
        description: this.ticket.description,
        issue_id: this.ticket.issue_id,
        status: this.ticket.status,
        attachments: this.ticket.attachments || [],
        department_id: this.ticket.department_id,
      }),
      files: [],
    }
  },
  watch: {
    files: {
      handler(files) {
        let toBeAdded = files.filter((file) => file != null)
        files.forEach((file, index) => {
          if (file == null) {
            this.delete(this.files, index)
          }
        })
        this.form.attachments = this.form.attachments.concat(toBeAdded)
      },
      deep: true,
    },
  },

  methods: {
    remove(index) {
      // send a inertia delete request to the server
      if (this.form.attachments[index].id == null) {
        this.form.attachments.splice(index, 1)
        this.files = []
        return
      }
      this.$inertia.delete(`/files/${this.form.attachments[index].id}`, {
        onSuccess: () => {
          this.form.attachments.splice(index, 1)
          this.files = []
        },
      })
    },

    delete(items, index) {
      items.splice(index, 1)
    },

    download(index) {
      let att = Document.createElement('a')
      att.href = this.form.attachments[index].url
      att.download = this.form.attachments[index].name
    },

    change(e) {
      this.form.attachments.push(e.target.files[0])
    },

    importAttachment() {
      this.$refs.file.click()
    },

    hasPermission(permission) {
      var userPermission = new PermissionChecker(this.$page.props)
      return userPermission.hasPermission(permission)
    },

    hasRole(role) {
      var userRole = new RoleChecker(this.$page.props)
      return userRole.hasRole(role)
    },

    update() {
      this.form.post(`/tickets/tickets/${this.ticket.id}`, {
        _method: 'put',
        onSuccess: () => this.$emit('saved'),
      })
    },

    destroy() {
      if (confirm('Are you sure you want to delete this ticket?')) {
        this.$inertia.delete(`/tickets/tickets/${this.ticket.id}`)
      }
    },

    restore() {
      if (confirm('Are you sure you want to restore this ticket?')) {
        this.$inertia.put(`/tickets/tickets/${this.ticket.id}/restore`)
      }
    },

    slugifyStatus(status) {
      return status.toLowerCase().replace(' ', '_')
    },
  },
}
</script>
